import React, { Fragment, useLayoutEffect } from "react";
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import { CITY_PATH, SECURITY_PROFILE_PATH, VIEW_PATH } from "../../../../config/paths";
import Content from "../../../layout/Content";
import Loader from "../../../common/Loader";
import NoDataResource from "../../../common/resource/NoDataResource";
import SecurityProfileToolbar from "./SecurityProfileToolbar";
import { clearSecurityProfile, getSecurityProfile } from "../../../../actions/security/SecurityProfile";
import ResourceProperty from "../../../common/resource/ResourceProperty";
import Table from "../../../common/Table";
import Alert from "../../../common/informative/Alert";
import { DEFAULT_PAGE_SIZE, SECURITY_VISIBILITY } from "../../../../config/constants";
import { filterMethodCaseInsensitive } from "../../../../utils/filter-methods";
import InternalResource from "../../../common/resource/InternalResource";
import { Col, Row } from "reactstrap";

const SecurityProfile = ({
  breadcrumbs,
  match: {
    params: { id },
  },
  securityProfile,
  loading,
  dispatchGetSecurityProfile,
  dispatchClearSecurityProfile,
}) => {
  useLayoutEffect(() => {
    dispatchGetSecurityProfile({ id });
    return () => dispatchClearSecurityProfile();
  }, []);

  let content;
  let toolbar;

  if (loading) content = <Loader />;
  else if (securityProfile.isEmpty())
    content = <NoDataResource returnPage={CITY_PATH} />;
  else {
    const securityProfileJson = securityProfile.toJS();

    const basicInformationResource = (
      <Fragment>
        <h3>Información Básica</h3>
        <ResourceProperty label="Nombre:">
          {securityProfileJson.name}
        </ResourceProperty>
        <ResourceProperty label="Descripción:">
          {securityProfileJson.description || '-'}
        </ResourceProperty>
        <ResourceProperty label="Visibilidad:">
          {securityProfileJson.securityVisibility
            ? SECURITY_VISIBILITY[securityProfileJson.securityVisibility].label
            : null}
        </ResourceProperty>
      </Fragment>
    );

    const authorizedViewData = securityProfileJson.viewAuthorizationList.map(
      ({view}) => ({
        id: view.id,
        name: view.name,
        route: view.route,
        description: view.description,
      })
    );

    const authorizedViewTable =
      authorizedViewData.length > 0 ? (
        <Table
          columns={[
            {
              Header: 'Nombre',
              accessor: 'name',
              className: 'text-center',
            },
            {
              Header: 'Ruta',
              accessor: 'route',
              className: 'text-center',
            },
            {
              Header: 'Descripción',
              accessor: 'description',
              className: 'text-center',
            },
          ]}
          data={authorizedViewData}
          defaultPageSize={
            authorizedViewData.length <= 10 ? authorizedViewData.length : DEFAULT_PAGE_SIZE
          }
          showPagination={authorizedViewData.length > 10}
          manual={false}
          modelPath={VIEW_PATH}
          filterable
          openPathInNewTab
          navigateToModelOnRowClick
          defaultFilterMethod={filterMethodCaseInsensitive}
        />
      ) : (
        <Alert message={'No hay vistas autorizadas'} type="warning" />
      );

    const authorizedViewResource = (
      <div className="mb-3">
        <h3>Vistas Autorizadas</h3>
        {authorizedViewTable}
      </div>
    );

    const authorizedEndpointData = securityProfileJson.authorizedEndpointList.map(
      ({ endpoint }) => ({
        id: endpoint.id,
        name: endpoint.name,
        route: endpoint.route,
        description: endpoint.description,
        permissionType: endpoint.permissionType,
      })
    );

    const authorizedEndpointTable =
      authorizedEndpointData.length > 0 ? (
        <Table
          columns={[
            {
              Header: 'Nombre',
              accessor: 'name',
              className: 'text-center',
            },
            {
              Header: 'Ruta',
              accessor: 'route',
              className: 'text-center',
            },
            {
              Header: 'Descripción',
              accessor: 'description',
              className: 'text-center',
            },
            {
              Header: 'Tipo de Permiso',
              accessor: 'permissionType',
              className: 'text-center',
            },
          ]}
          data={authorizedEndpointData}
          defaultPageSize={
            authorizedEndpointData.length <= 10
              ? authorizedEndpointData.length
              : DEFAULT_PAGE_SIZE
          }
          showPagination={authorizedEndpointData.length > 10}
          manual={false}
          filterable
          defaultFilterMethod={filterMethodCaseInsensitive}
        />
      ) : (
        <Alert
          message={'No hay endpoints autorizados'}
          type="warning"
        />
      );

    const authorizedEndpointResource = (
      <div className="mb-3">
        <h3>Punto de Acceso Autorizados</h3>
        {authorizedEndpointTable}
      </div>
    );

    const internalResource = (
      <InternalResource
        id={securityProfileJson.id}
        createDate={securityProfileJson.createDate}
        lastUpdate={securityProfileJson.lastUpdate}
      />
    );

    content = (
      <>
        <Row>
          <Col md={6}>{basicInformationResource}</Col>
        </Row>
        {authorizedViewResource}
        {authorizedEndpointResource}
        <Row>
          <Col md={6}>{internalResource}</Col>
        </Row>
      </>
    );

    toolbar = (
      <SecurityProfileToolbar
        securityProfileId={securityProfileJson.id}
      />
    );
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      toolbar={toolbar}
      title="Perfil de Seguridad"
      content={content}
    />
  );
};

SecurityProfile.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchGetSecurityProfile: PropTypes.func.isRequired,
  dispatchClearSecurityProfile: PropTypes.func.isRequired,
  securityProfile: PropTypes.instanceOf(Immutable.Map).isRequired,
  match: matchPropTypes.isRequired,
  loading: PropTypes.bool,
};

const mapDispatchToProps = {
  dispatchGetSecurityProfile: getSecurityProfile,
  dispatchClearSecurityProfile: clearSecurityProfile,
};


SecurityProfile.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ SecurityUnit }) => ({
  breadcrumbs: [
    ...SecurityUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Perfiles de Seguridad',
      href: SECURITY_PROFILE_PATH ,
    },
    {
      text: 'Ver',
      href: '',
    },
  ],
  securityProfile: SecurityUnit.SecurityProfile.getIn(['current', 'content']),
  loading: !SecurityUnit.SecurityProfile.getIn(['current', 'activity']).isEmpty(),
});


export default connect(mapStateToProps, mapDispatchToProps)(SecurityProfile);